<template>
  <div id="Coders">
    <el-row class="header-row">
      <h3 class="el-page-header">Coders</h3>
    </el-row>
    <el-form label-position="left" label-width="150px" size="mini">
      <div v-if="!showAddCoderForm">
        <el-button-group>
          <el-button class="hiaButton" plain round size="mini" @click="toggleAddCoderForm()" icon="el-icon-plus">Add Coder</el-button>
          <el-button :disabled="disableMergeButton" class="hiaButton" plain round size="mini" @click="toggleMergeCoder()">Merge
            Multiple</el-button>
        </el-button-group>
      </div>
      <el-row v-if="showAddCoderForm">
        <el-col :lg="8" :md="12">
          <el-form :model="addCoderForm" ref="addCoderForm" size="mini" :rules="addCoderForm.rules" label-position="left" label-width="180px" @submit.native.prevent>
            <el-form-item prop="name" label="Name">
              <el-input v-model="addCoderForm.name" size="mini"></el-input>
            </el-form-item>
            <el-form-item>
              <el-button-group>
                <el-button class="hiaButton" plain round size="mini" type="text" @click="toggleAddCoderForm()">Cancel</el-button>
                <el-button class="hiaButton" plain round size="mini" @click="addCoder">Add</el-button>
              </el-button-group>
            </el-form-item>
          </el-form>
        </el-col>
      </el-row>
      <el-row class="topMargin">
        <el-col :lg="16" :md="22">
          <v-client-table class="hiaTable" v-loading="loading" element-loading-spinner="atom-audit-loader" ref="coderListsGrid" id="coderListsGrid" :data="coderList" :columns="coderColumns" :options="coderOptions" uniqueKey="coderId">
            <div slot="filter__inactive">
              <input type="checkbox" @change="$refs.coderListsGrid.setFilter({inactive: $event.target.checked})">
            </div>
            <template slot="h__email">
              Email <el-tooltip effect="dark" content="Linked user email address, users can be linked to coders via the User Admin menu." placement="top">
                <i class="el-icon-info"></i>
              </el-tooltip>
            </template>
            <template slot="h__aliases">
              Aliases <el-tooltip effect="dark" content="Alternate names that can later resolve to this coder during import." placement="top">
                <i class="el-icon-info"></i>
              </el-tooltip>
            </template>
            <template slot="selected" slot-scope="props">
              <div class="el-input--mini">
                <input type="checkbox" v-model="props.row.selected" @click="selectRecord(props.row, $event)">
              </div>
            </template>
            <template slot="name" slot-scope="props">
              <div class="el-input--mini">
                <input class="el-input__inner" type="text" v-model="props.row.name" @change="updateCoder(props.row)">
              </div>
            </template>
            <template slot="aliases" slot-scope="props">
              <span class="alias-chips">
                <span v-for="alias in (props.row.aliases || [])" :key="alias.coderAliasId" class="alias-chip">
                  {{ alias.alias }}
                  <i class="el-icon-close delete-button" @click="deleteAlias(props.row, alias)"></i>
                </span>
                <span class="aliases-tag" @click.stop="openAddAliasPopover(props.row, $event)">+ Alias</span>
              </span>
            </template>
            <template slot="inactive" slot-scope="props">
              <input :key="props.row.coderId" type="checkbox" v-model="props.row.inactive" @change="updateCoder(props.row)">
            </template>
            <template slot="email" slot-scope="props">
              {{ props.row.users.length > 0 ? props.row.users.map(x => x.email).join(', ') : null }}
            </template>
            <template slot="delete" slot-scope="props">
              <div style="text-align: center;">
                <i class="el-icon-close delete-button" @click="deleteCoder(props.row)"></i>
              </div>
            </template>
          </v-client-table>
        </el-col>
      </el-row>
    </el-form>

    <el-dialog title="Merge Coders" :visible.sync="showMergeCoder" width="60%">
      <div v-loading="mergeCoderLoading" element-loading-spinner="atom-audit-loader">
        Select the target coder record to merge to:
        <table class="mergeCoderTable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="coder in coderList.filter(x => x.selected).sort((a,b) => a.name < b.name ? -1 : 1)"
              :key="coder.coderId"
              class="mergeCoderRow"
              :class="{ selected: coder.coderId === selectedMergeCoder.coderId }"
              @click="selectedMergeCoder = coder"
            >
              <td class="mergeCoderName">{{ coder.name }}</td>
              <td class="mergeCoderEmail">{{ formatCoderEmail(coder) }}</td>
              <td><i v-if="coder.coderId === selectedMergeCoder.coderId" class="mdi mdi-check mergeCoderCheck"></i></td>
            </tr>
          </tbody>
        </table>
        <span v-if="selectedMergeCoder.name">Merge all listed coders to {{ formatMergeTargetLabel(selectedMergeCoder) }}</span>
        <div style="margin-bottom: 40px;">
          <div class="pull-right">
            <el-button-group>
              <el-button class="hiaButton" plain round size="mini" type="text" @click="toggleMergeCoder">Cancel</el-button>
              <el-button :disabled="!selectedMergeCoder.name" class="hiaButton" plain round size="mini" @click="doMergeCoder">Continue
              </el-button>
            </el-button-group>
          </div>
        </div>
      </div>
    </el-dialog>

    <el-popover
      ref="addAliasPopover"
      placement="right"
      :title="addAliasPopoverTitle"
      width="300"
      trigger="manual"
      v-model="showAddAliasPopover"
    >
      <el-form :model="addAliasForm" ref="addAliasForm" size="mini" :rules="addAliasForm.rules" label-position="top" hide-required-asterisk @submit.native.prevent>
        <el-form-item prop="alias" label="Alias">
          <el-input v-model="addAliasForm.alias" size="mini" @keyup.enter.native="addAlias"></el-input>
        </el-form-item>
        <el-form-item>
          <el-button-group>
            <el-button class="hiaButton" plain round size="mini" type="text" @click="closeAddAliasPopover">Cancel</el-button>
            <el-button class="hiaButton" plain round size="mini" @click="addAlias">Add</el-button>
          </el-button-group>
        </el-form-item>
      </el-form>
    </el-popover>
  </div>
</template>

<script>
  import { mapState, mapActions, mapMutations } from 'vuex'
  import HiaAuthorization from '@/mixins/hiaAuthorization'
  import hiaTable from '@/mixins/hiaTable'

  export default {
    name: 'coderTab',
    mixins: [hiaTable, HiaAuthorization],
    props: [],
    data() {
      return {
        loading: false,
        showMergeCoder: false,
        mergeCoderLoading: false,
        selectedMergeCoder: {},
        localCoder: [],
        showAddAliasPopover: false,
        addAliasCoder: {},
        addAliasReference: null,
        addAliasForm: {
          alias: '',
          rules: {
            alias: [
              { required: true, message: 'Please Input Alias', trigger: 'none' }
            ]
          }
        },
        coderColumns: ['selected', 'name', 'aliases', 'email', 'inactive', 'delete'],
        coderOptions: {
          filterByColumn: true,
          sortable: ['name'],
          orderBy: {
            column: 'name',
            ascending: true
          },
          filterAlgorithm: {
            email(row, query) {
              return row.users.some(x => x.email.toUpperCase().includes(query.toUpperCase()))
            },
            aliases(row, query) {
              return (row.aliases || []).some(x => x.alias.toUpperCase().includes(query.toUpperCase()))
            }
          },
          headings: {
            delete: function (h) {
              return ''
            },
            selected: (h) => {
              return h('input', {
                attrs: {
                  type: 'checkbox',
                  id: 'coderSelectAllCheckbox',
                  disabled: false // this.readOnly()
                },
                on: {
                  click: (e) => {
                    this.localselectAll(e.srcElement.checked)
                  }
                },
                ref: 'selectAllCheckbox'
              })
            }
          },
          perPage: 10,
          texts: {
            filter: '',
            filterBy: '...'
          },
          highlightMatches: true,
          sortIcon: { is: 'none' }
        },
        showAddCoderForm: false,
        addCoderForm: {
          name: '',
          rules: {
            name: [
              { required: true, message: 'Please Input Name', trigger: 'none' }
            ]
          }
        }
      }
    },
    methods: {
      ...mapActions('managedLists/coders/', ['GET_CODERS', 'POST_CODER', 'DELETE_CODER', 'PUT_CODER', 'PUT_MERGE_CODER', 'POST_CODER_ALIAS', 'DELETE_CODER_ALIAS']),
      ...mapMutations('managedLists/coders/', ['SET_SELECTED']),
      initialize(route) {
        this.loading = true
        this.GET_CODERS().then(response => {
          this.localCoder = JSON.parse(JSON.stringify(this.coderList))
          this.loading = false
        })
      },
      localselectAll(checked) {
        let filteredRecords = []

        if (this.$refs.coderListsGrid) {
          filteredRecords = this.$refs.coderListsGrid.allFilteredData
        }

        const recs = this.coderList.filter(x => filteredRecords.some(y => y.coderId === x.coderId))
        hiaTable.selectAll(recs, checked)
        this.SET_SELECTED(recs)
      },
      selectRecord(row, e) {
        const checked = e.srcElement.checked
        row.selected = checked
        this.SET_SELECTED([row])
      },
      addCoder() {
        this.$refs.addCoderForm.validate(async (valid) => {
          if (valid) {
            try {
              this.loading = true
              const payload = {
                coder: {
                  name: this.addCoderForm.name
                }
              }
              await this.POST_CODER(payload)
              this.$refs.addCoderForm.resetFields()
              this.toggleAddCoderForm()
              this.loading = false
              this.$message({
                message: 'Coder Added',
                type: 'success'
              })
            } catch (err) {
              this.loading = false
              this.$message({
                message: `Error Adding Coder: ${err.message}`,
                type: 'error'
              })
            }
          }
        })
      },
      async updateCoder(coder) {
        const index = this.coderList.findIndex(x => x.coderId === coder.coderId)
        this.$set(this.coderList, index, coder)

        try {
          this.loading = true
          await this.PUT_CODER(coder)
          this.loading = false
          this.$message({
            message: 'Coder Saved',
            type: 'success'
          })
        } catch (err) {
          this.loading = false
          this.$message({
            message: 'Error Editing Coder',
            type: 'error'
          })
        }
      },
      async deleteCoder(coder) {
        await this.$confirm(`Permanently delete ${coder.name}?`, 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })

        try {
          this.loading = true
          await this.DELETE_CODER(coder)
          this.loading = false
          this.$message({
            message: 'Coder Deleted Successfully',
            type: 'success'
          })
        } catch (err) {
          this.loading = false
          this.$message({
            message: `Error Deleting Coder: ${err.message}`,
            type: 'error'
          })
        }
      },
      toggleAddCoderForm() {
        this.showAddCoderForm = !this.showAddCoderForm
      },
      formatCoderEmail(coder) {
        if (!coder?.users?.length) return ''
        return coder.users.map(x => x.email).join(', ')
      },
      formatMergeTargetLabel(coder) {
        const name = coder?.name
        if (!name) return ''
        const email = this.formatCoderEmail(coder)
        return email ? `${name} (${email})` : name
      },
      openAddAliasPopover(coder, e) {
        const popper = this.$refs.addAliasPopover
        if (!popper) return

        const tag = e.currentTarget
        if (this.showAddAliasPopover && this.addAliasReference === tag) {
          this.closeAddAliasPopover()
          return
        }

        this.addAliasCoder = coder
        this.addAliasReference = tag
        this.addAliasForm.alias = ''
        popper.doDestroy()
        popper.referenceElm = tag
        this.showAddAliasPopover = true
        this.$nextTick(() => {
          popper.doShow()
          if (this.$refs.addAliasForm) {
            this.$refs.addAliasForm.clearValidate()
          }
          this.bindAddAliasOutsideClick()
        })
      },
      closeAddAliasPopover() {
        const popper = this.$refs.addAliasPopover
        this.showAddAliasPopover = false
        this.addAliasForm.alias = ''
        this.addAliasReference = null
        this.unbindAddAliasOutsideClick()
        if (popper) {
          popper.doDestroy()
          popper.doClose()
        }
        this.$nextTick(() => {
          if (this.$refs.addAliasForm) {
            this.$refs.addAliasForm.clearValidate()
          }
        })
      },
      bindAddAliasOutsideClick() {
        this.unbindAddAliasOutsideClick()
        this._addAliasOutsideClick = (event) => {
          const popper = this.$refs.addAliasPopover
          const popperEl = popper && popper.$refs.popper
          const reference = popper && popper.referenceElm
          if (popperEl && popperEl.contains(event.target)) return
          if (reference && reference.contains(event.target)) return
          this.closeAddAliasPopover()
        }
        document.addEventListener('mousedown', this._addAliasOutsideClick)
      },
      unbindAddAliasOutsideClick() {
        if (this._addAliasOutsideClick) {
          document.removeEventListener('mousedown', this._addAliasOutsideClick)
          this._addAliasOutsideClick = null
        }
      },
      addAlias() {
        this.$refs.addAliasForm.validate(async (valid) => {
          if (valid) {
            const coderId = this.addAliasCoder.coderId
            const alias = this.addAliasForm.alias
            this.closeAddAliasPopover()
            try {
              this.loading = true
              await this.POST_CODER_ALIAS({
                coderId,
                alias: {
                  coderId,
                  alias
                }
              })
              this.loading = false
              this.$message({
                message: 'Alias Added',
                type: 'success'
              })
            } catch (err) {
              this.loading = false
              this.$message({
                message: `Error Adding Alias: ${err.message}`,
                type: 'error'
              })
            }
          }
        })
      },
      async deleteAlias(coder, alias) {
        await this.$confirm(`Permanently delete alias ${alias.alias}?`, 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })

        try {
          this.loading = true
          await this.DELETE_CODER_ALIAS({
            coderId: coder.coderId,
            alias
          })
          this.loading = false
          this.$message({
            message: 'Alias Deleted Successfully',
            type: 'success'
          })
        } catch (err) {
          this.loading = false
          this.$message({
            message: `Error Deleting Alias: ${err.message}`,
            type: 'error'
          })
        }
      },
      toggleMergeCoder() {
        this.selectedMergeCoder = {}
        this.showMergeCoder = !this.showMergeCoder
      },
      doMergeCoder() {
        this.$confirm(`Update all existing Review Records for listed Coder to ${this.formatMergeTargetLabel(this.selectedMergeCoder)}?`, 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          this.mergeCoderLoading = true
          const payload = {
            targetCoderId: this.selectedMergeCoder.coderId,
            coder: this.coderList.filter(x => x.selected && x.coderId !== this.selectedMergeCoder.coderId)
          }
          this.PUT_MERGE_CODER(payload).then(response => {
            this.toggleMergeCoder()
            this.localselectAll(false)

            const table = this.$refs.coderListsGrid.$el
            const checkboxes = table.querySelectorAll('input[type ="checkbox"]')

            for (let i = 0; i < checkboxes.length; i++) {
              checkboxes[i].checked = false
            }

            this.mergeCoderLoading = false
          }, error => {
            console.log(error)
            this.mergeCoderLoading = false
          })
        })
      }
    },
    computed: {
      ...mapState('managedLists/clients/', ['clientList']),
      ...mapState('managedLists/coders/', ['coderList', 'coderListLoading']),
      ...mapState('user/', ['currentTenantGuid']),
      disableMergeButton() {
        return this.coderList.filter(x => x.selected).length < 2
      },
      addAliasPopoverTitle() {
        return this.addAliasCoder.name ? `Add Alias to ${this.addAliasCoder.name}` : 'Add Alias'
      }
    },
    watch: {
      currentTenantGuid() {
        this.closeAddAliasPopover()
        this.initialize(this.$route)
      }
    },
    created: function () {
      this.initialize(this.$route)
    },
    beforeDestroy() {
      this.unbindAddAliasOutsideClick()
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.mergeCoderRow {
  cursor: pointer;
}

.mergeCoderRow:hover td,
.mergeCoderRow.selected td {
  background: rgba(255, 196, 12, 0.2);
}

.mergeCoderRow:hover td:first-child,
.mergeCoderRow.selected td:first-child {
  border-radius: 2px 0 0 2px;
}

.mergeCoderRow:hover td:last-child,
.mergeCoderRow.selected td:last-child {
  border-radius: 0 2px 2px 0;
}

.mergeCoderName {
  padding: 0px 20px 0px 20px;
  color: #337ab7;
}

.topMargin {
  margin: 10px 0px 0px 0px;
}

.mergeCoderTable {
  margin: 20px 0px 20px 0px;
  border-collapse: collapse;
}

.mergeCoderTable thead th {
  text-align: left;
  padding: 0px 20px 8px 20px;
  font-weight: 600;
}

.mergeCoderTable td {
  text-align: left;
}

.mergeCoderEmail {
  padding: 0px 20px 0px 20px;
  color: #606266;
}

.mergeCoderCheck {
  color: #63b344;
  margin-left: 8px;
}

.alias-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.alias-chip,
.aliases-tag {
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: 22px;
  min-height: 22px;
  font-size: 12px;
  line-height: 22px;
  border-radius: 4px;
  vertical-align: middle;
  transition: none;
}

.alias-chip {
  padding: 0 6px 0 8px;
  background: #dcdcdd;
  border: 1px solid #d2d2d3;
  color: #606266;
}

.alias-chip .delete-button {
  margin-left: 4px;
  font-size: 12px;
}

.alias-chip .delete-button:hover {
  color: #303133;
}

.aliases-tag {
  padding: 0 8px;
  background: #e6eef4;
  border: 1px solid #c5d4e0;
  color: #005695;
  cursor: pointer;
}

.aliases-tag:hover {
  border-color: #005695;
}
</style>
